import {
  Autocomplete,
  Box,
  ButtonBase,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { Fragment, useEffect, useId, useMemo, useState } from "react";

import {
  buildProfileDetailRows,
  type ProfileDetailRow,
} from "./EmailPreviewModalUserLookup.utils";

import {
  recognitionUsersQueryOptions,
  useRecognitionUser,
  type RecognitionUser,
} from "@/api/profiles";
import {
  AudienceTableContainer,
  HeaderCell,
  RowCell,
} from "@/features/Audiences/AudienceTable";
import { alternatingRowBg } from "@/features/Audiences/tableUtils";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { ChevronRightIcon, SearchIcon, PersonIcon } from "@/icons";

const MIN_SEARCH_CHARS = 3;
const PAGE_SIZE = 10;

function displayName(user: RecognitionUser): string {
  if (user.firstName && user.lastName) {
    return `${user.firstName} ${user.lastName}`;
  }
  return user.email || user.phone || "-";
}

function recognitionOptionLabel(user: RecognitionUser): string {
  return user.email || displayName(user);
}

function ProfileDetailsTable({ rows }: { rows: ProfileDetailRow[] }) {
  const idPrefix = useId();
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const childRowId = (childKey: string) => `${idPrefix}-${childKey}`;

  const toggle = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });

  return (
    <AudienceTableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <HeaderCell colSpan={2} size="small">
              Profile details
            </HeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => {
            const backgroundColor = alternatingRowBg(index);
            // An empty array would render a toggle with nothing behind it.
            const children = row.children?.length ? row.children : undefined;
            const open = children ? expanded.has(row.key) : false;
            return (
              <Fragment key={row.key}>
                <TableRow sx={{ backgroundColor }}>
                  <RowCell>
                    {children ? (
                      <ButtonBase
                        aria-expanded={open}
                        aria-controls={childRowId(children[0].key)}
                        onClick={() => toggle(row.key)}
                        sx={{ gap: "4px", font: "inherit", color: "inherit" }}
                      >
                        <ChevronRightIcon
                          sx={(theme) => ({
                            fontSize: 16,
                            transform: open ? "rotate(90deg)" : "none",
                            transition: theme.transitions.create("transform"),
                          })}
                        />
                        {row.label}
                      </ButtonBase>
                    ) : (
                      row.label
                    )}
                  </RowCell>
                  <RowCell align="right" sx={{ wordBreak: "break-word" }}>
                    {row.value}
                  </RowCell>
                </TableRow>
                {open &&
                  children?.map((child) => (
                    <TableRow
                      key={child.key}
                      id={childRowId(child.key)}
                      sx={{ backgroundColor }}
                    >
                      <RowCell sx={{ pl: 10 }}>{child.label}</RowCell>
                      <RowCell align="right" sx={{ wordBreak: "break-word" }}>
                        {child.value}
                      </RowCell>
                    </TableRow>
                  ))}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
    </AudienceTableContainer>
  );
}

export function EmailPreviewModalUserLookup({
  siteId,
  onCommittedPreviewUserChange,
}: {
  siteId: string;
  onCommittedPreviewUserChange?: (user: RecognitionUser | null) => void;
}) {
  const [inputValue, setInputValue] = useState("");
  const [selectedUser, setSelectedUser] = useState<RecognitionUser | null>(
    null,
  );
  const debouncedInput = useDebouncedValue(inputValue, 300);

  const searchQuery = useMemo(() => {
    const trimmed = debouncedInput.trim();
    return trimmed.length >= MIN_SEARCH_CHARS ? trimmed : undefined;
  }, [debouncedInput]);

  const { data, isLoading, isFetching, error } = useQuery({
    ...recognitionUsersQueryOptions(siteId, {
      page: 1,
      pageSize: PAGE_SIZE,
      q: searchQuery,
    }),
    enabled: Boolean(siteId && searchQuery),
  });

  const options = searchQuery ? (data?.users ?? []) : [];
  const loading = Boolean(searchQuery) && (isLoading || isFetching);

  /** Like the MUI async demo: keep `value` stable while typing; only treat as committed when input matches the option label. */
  const committedPreviewUser = useMemo(() => {
    if (!selectedUser) return null;
    const label = recognitionOptionLabel(selectedUser);
    return inputValue.trim() === label.trim() ? selectedUser : null;
  }, [selectedUser, inputValue]);

  useEffect(() => {
    onCommittedPreviewUserChange?.(committedPreviewUser);
  }, [committedPreviewUser, onCommittedPreviewUserChange]);

  // Disabled while no user is committed: the hook skips an empty userId.
  const { data: userDetail } = useRecognitionUser(
    siteId,
    committedPreviewUser?.id ?? "",
  );

  // The detail record has every field; until it loads (or if it fails), show
  // the search row. `customer` only exists on the search row, so carry it over.
  const detailRows = useMemo(() => {
    if (!committedPreviewUser) return [];
    return buildProfileDetailRows(
      userDetail
        ? { ...userDetail, customer: committedPreviewUser.customer }
        : committedPreviewUser,
    );
  }, [committedPreviewUser, userDetail]);

  return (
    <Stack
      spacing={4}
      sx={{
        bgcolor: "background.paper-elevation-2",
        borderRadius: 1,
        p: 4,
      }}
    >
      <Typography variant="subtitle1">
        Send a preview using a profile
      </Typography>
      <Stack useFlexGap sx={{ width: "100%", gap: 4, mt: 7 }}>
        <Box>
          <Typography
            component="label"
            htmlFor="preview-profile-search"
            variant="body2"
            sx={{ display: "block", mb: 2 }}
          >
            Select a profile for the preview
          </Typography>
          <Autocomplete<RecognitionUser, false, false, false>
            id="preview-profile-search"
            forcePopupIcon={false}
            options={options}
            getOptionLabel={recognitionOptionLabel}
            filterOptions={(opts) => opts}
            inputValue={inputValue}
            onInputChange={(_, newInputValue) => {
              setInputValue(newInputValue);
              if (newInputValue === "") {
                setSelectedUser(null);
              }
            }}
            value={selectedUser}
            onChange={(_, value) => {
              setSelectedUser(value);
              if (value) {
                setInputValue(recognitionOptionLabel(value));
              } else {
                setInputValue("");
              }
            }}
            loading={loading}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            noOptionsText={
              searchQuery
                ? loading
                  ? "Searching…"
                  : "No matching users"
                : `Type at least ${MIN_SEARCH_CHARS} characters`
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search by name, email, or phone number"
                size="small"
                variant="outlined"
                fullWidth
                slotProps={{
                  input: {
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                    inputProps: {
                      ...params.inputProps,
                      autoComplete: "one-time-code",
                      "data-1p-ignore": true,
                      "data-bwignore": true,
                      "data-lpignore": true,
                      "data-form-type": "other",
                    },
                  },
                }}
              />
            )}
          />
        </Box>

        {error ? (
          <Typography variant="body2" color="error">
            Something went wrong while searching. Try again.
          </Typography>
        ) : null}

        <Stack spacing={2} useFlexGap>
          <Typography variant="body2">Previewing as</Typography>
          <Box
            sx={{
              border: "1px solid",
              borderColor: "common.white",
              borderRadius: 1,
              p: 4,
            }}
          >
            {committedPreviewUser ? (
              <Stack useFlexGap spacing={2}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={2}
                  useFlexGap
                >
                  <PersonIcon />
                  <Typography variant="subtitle1">
                    {displayName(committedPreviewUser)}
                  </Typography>
                </Stack>
                <Typography variant="body2">
                  {committedPreviewUser.email || "-"}
                </Typography>
              </Stack>
            ) : (
              <Typography variant="body2">
                Select a profile from the search results.
              </Typography>
            )}
          </Box>
        </Stack>

        {committedPreviewUser && detailRows.length > 0 ? (
          <ProfileDetailsTable
            key={committedPreviewUser.id}
            rows={detailRows}
          />
        ) : null}
      </Stack>
    </Stack>
  );
}
